import { Fragment } from "react";

import { useGridLayout } from "./hooks/useGridLayout";
import { generateZoneId } from "./utils/gridUtils";
import { GridBlockProps } from "./types";
import { useTheme } from "@mui/material/styles";
import { Box, Stack, useMediaQuery } from "@mui/material";
import { resolveRadius, resolveSpacing } from "../../layoutTokens";

export const GridBlock = (props: GridBlockProps) => {
    const {
        id = "",
        columns = 1,
        rows = 1,
        spacing = 2,
        rowGap,
        columnGap,
        grow = false,
        columnWidth = "equal",
        gridPadding,
        cellPadding,
        defaultCellBackgroundColor,
        defaultCellBorderColor,
        defaultCellBorderWidth = 0,
        defaultCellBorderRadius = 0,
        cellLayouts,
        paddingHorizontal = 0,
        paddingVertical = 0,
        backgroundColor,
        borderColor,
        borderWidth = 0,
        borderRadius = 0,
        ...slotProps
    } = props;

    const { colIndices, rowIndices, gridStyle } = useGridLayout({
        columns,
        rows,
        spacing,
        rowGap,
        columnGap,
        grow,
        columnWidth,
    });
    const MIN_CELL_HEIGHT = 300;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const resolvedBorderRadius = resolveRadius(theme, borderRadius);

    const effectiveGridPadding = {
        top: typeof gridPadding?.top === "number" ? gridPadding.top : 0,
        right: typeof gridPadding?.right === "number" ? gridPadding.right : 0,
        bottom: typeof gridPadding?.bottom === "number" ? gridPadding.bottom : 0,
        left: typeof gridPadding?.left === "number" ? gridPadding.left : 0,
    };

    const effectiveDefaultCellPadding = {
        top: typeof cellPadding?.top === "number" ? cellPadding.top : paddingVertical,
        right: typeof cellPadding?.right === "number" ? cellPadding.right : paddingHorizontal,
        bottom: typeof cellPadding?.bottom === "number" ? cellPadding.bottom : paddingVertical,
        left: typeof cellPadding?.left === "number" ? cellPadding.left : paddingHorizontal,
    };

    const resolvedBorderColor = borderColor || theme.palette.divider;
    const resolvedBorder = borderWidth > 0 ? `${borderWidth}px solid ${resolvedBorderColor}` : undefined;

    return (
        <Box
            style={gridStyle}
            data-grid-id={id}
            sx={{
                backgroundColor: backgroundColor || undefined,
                border: resolvedBorder,
                borderRadius: resolvedBorderRadius,
                paddingTop: resolveSpacing(theme, effectiveGridPadding.top),
                paddingRight: resolveSpacing(theme, effectiveGridPadding.right),
                paddingBottom: resolveSpacing(theme, effectiveGridPadding.bottom),
                paddingLeft: resolveSpacing(theme, effectiveGridPadding.left),
            }}
        >
            {rowIndices.map((rowIndex: number) => (
                <Fragment key={`${id}-row-${rowIndex}`}>
                    {colIndices.map((colIndex: number) => {
                        const zoneId = generateZoneId(rowIndex, colIndex);
                        const SlotField = slotProps[zoneId as keyof typeof slotProps];

                        const layoutKey = `cell-${rowIndex}-${colIndex}`;
                        const cellLayout = cellLayouts?.[layoutKey] ?? {};
                        const cellPaddingOverride = cellLayout.padding;

                        const effectiveCellPadding = {
                            top:
                                typeof cellPaddingOverride?.top === "number" ?
                                    cellPaddingOverride.top
                                :   effectiveDefaultCellPadding.top,
                            right:
                                typeof cellPaddingOverride?.right === "number" ?
                                    cellPaddingOverride.right
                                :   effectiveDefaultCellPadding.right,
                            bottom:
                                typeof cellPaddingOverride?.bottom === "number" ?
                                    cellPaddingOverride.bottom
                                :   effectiveDefaultCellPadding.bottom,
                            left:
                                typeof cellPaddingOverride?.left === "number" ?
                                    cellPaddingOverride.left
                                :   effectiveDefaultCellPadding.left,
                        };

                        const resolvedCellBackgroundColor =
                            typeof cellLayout.backgroundColor === "string" ?
                                cellLayout.backgroundColor
                            :   defaultCellBackgroundColor;

                        const resolvedCellBorderColor =
                            (typeof cellLayout.borderColor === "string" ?
                                cellLayout.borderColor
                            :   defaultCellBorderColor) || theme.palette.divider;

                        const resolvedCellBorderWidth =
                            typeof cellLayout.borderWidth === "number" ?
                                cellLayout.borderWidth
                            :   defaultCellBorderWidth;
                        const resolvedCellBorder =
                            resolvedCellBorderWidth > 0 ?
                                `${resolvedCellBorderWidth}px solid ${resolvedCellBorderColor}`
                            :   undefined;
                        const resolvedCellRadius = resolveRadius(
                            theme,
                            typeof cellLayout.borderRadius === "number" ?
                                cellLayout.borderRadius
                            :   defaultCellBorderRadius,
                        );

                        return (
                            <Stack
                                key={`${id}-cell-${rowIndex}-${colIndex}`}
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    justifySelf: "center",
                                    maxWidth: isMobile ? "100%" : theme.breakpoints.values.xl,
                                    backgroundColor: resolvedCellBackgroundColor || undefined,
                                    border: resolvedCellBorder,
                                    borderRadius: resolvedCellRadius,
                                    paddingTop: resolveSpacing(theme, effectiveCellPadding.top),
                                    paddingRight: resolveSpacing(theme, effectiveCellPadding.right),
                                    paddingBottom: resolveSpacing(theme, effectiveCellPadding.bottom),
                                    paddingLeft: resolveSpacing(theme, effectiveCellPadding.left),
                                    // No choice here, we are obligated to make an class injection in Puck Slot component and
                                    // override aggressively the minHeight property since the style are not exposed
                                    "& > div[class*='_DropZone_'], & > div[class*='_SlotField_']": {
                                        minHeight: `${MIN_CELL_HEIGHT}px !important`,
                                        width: "100% !important",
                                    },
                                    "& > div[class*='_DropZone_'] > *, & > div[class*='_SlotField_'] > *": {
                                        width: "100% !important",
                                        maxWidth: "100%",
                                    },
                                    // Assurer que les images sont responsive
                                    "& img": {
                                        maxWidth: "100%",
                                        height: "auto",
                                    },
                                }}
                            >
                                {SlotField && <SlotField />}
                            </Stack>
                        );
                    })}
                </Fragment>
            ))}
        </Box>
    );
};
